/**
 * T-009 — Pre-flight locate: the Word-facing seam around the pure `planPreflight` brain.
 *
 * Reads only. Runs every op's `Range.search` in a batch (one `context.sync`, not one-per-op), feeds
 * the match counts into `planPreflight`, and — only if the whole set resolves — hands back the
 * chosen Word `Range` proxies (registered as tracked objects so they survive into the apply pass).
 * If anything fails to locate, it returns the failure and mutates nothing: the paragraph is left
 * byte-identical. Verified in real Word (Stage 4 is manual by design).
 *
 * `Word` is referenced only inside functions, so importing this never breaks the Vitest environment.
 */

import { LocatedOp, isWordSearchable } from './locateOps';
import { planPreflight, PreflightPlan, MatchCounts } from './preflightPlan';

/* global Word */

/** A resolved op paired with the live Word range the executor will act on (null for start-inserts). */
export interface ResolvedRange {
  opIndex: number;
  op: LocatedOp;
  /** The chosen search match, tracked; null when the op needs no search (insert at paragraph start). */
  range: Word.Range | null;
}

export interface PreflightLocateResult {
  plan: PreflightPlan;
  /** Populated only when `plan.ok`; ordered right-to-left, matching `plan.resolved`. */
  ranges: ResolvedRange[];
}

/**
 * The search value an op needs located (delete → its value; insert → its anchor text; null when no
 * search is required).
 */
function searchValueOf(op: LocatedOp): string | null {
  if (op.kind === 'delete') return op.value;
  if (op.kind === 'fieldDelete') return null; // resolved by field range, not search
  return op.anchor.type === 'after' || op.anchor.type === 'before' ? op.anchor.value : null;
}

/**
 * Resolve all op target ranges read-only, or abort. Must be called inside a `Word.run`.
 *
 * @param context   active request context
 * @param paragraph target paragraph
 * @param located   located ops from T-008
 */
export async function preflightLocate(
  context: Word.RequestContext,
  paragraph: Word.Paragraph,
  located: LocatedOp[],
): Promise<PreflightLocateResult> {
  const whole = paragraph.getRange('Whole');

  // An op is "unsearchable" if its value is oversize or contains characters Range.search rejects.
  // We must NOT hand such a value to Word.search (it throws SearchStringInvalidOrTooLong and blows
  // up the whole batch); instead we mark it so planPreflight fails it cleanly.
  const unsearchable = located.map((op) => {
    const value = searchValueOf(op);
    if (value === null) return false; // no search needed (start-insert)
    if (op.kind === 'delete' && op.oversize) return true;
    return !isWordSearchable(value);
  });

  // De-duplicate identical, searchable values so each distinct string is searched once.
  const searchByValue = new Map<string, Word.RangeCollection>();
  located.forEach((op, i) => {
    if (unsearchable[i]) return;
    const value = searchValueOf(op);
    if (value !== null && value.length > 0 && !searchByValue.has(value)) {
      const results = whole.search(value, { matchCase: true, matchWholeWord: false });
      results.load('items');
      searchByValue.set(value, results);
    }
  });
  await context.sync(); // single batched sync for all searches

  // Match count per op (aligned to `located`), then the pure decision.
  const counts: MatchCounts = located.map((op, i) => {
    if (unsearchable[i]) return 0;
    const value = searchValueOf(op);
    if (value === null) return null; // start-insert: no search needed
    return searchByValue.get(value)?.items.length ?? 0;
  });

  const plan = planPreflight(located, counts, unsearchable);

  // Bind every RESOLVED op to its live range (skipped ops are simply absent). Partial applies are
  // supported: we still bind and apply whatever resolved even when some ops were skipped.
  // Bind each resolution to its live range and keep the proxy alive for the apply pass.
  const ranges: ResolvedRange[] = plan.resolved.map((res) => {
    const value = searchValueOf(res.op);
    let range: Word.Range | null = null;
    if (value !== null && res.matchIndex !== null) {
      range = searchByValue.get(value)!.items[res.matchIndex];
      // Keep the proxy alive across syncs into the apply pass. Cast sidesteps a known office-js
      // typings quirk where Word.Range isn't recognized as an OfficeExtension.ClientObject.
      (context.trackedObjects.add as (o: Word.Range) => void)(range);
    }
    return { opIndex: res.opIndex, op: res.op, range };
  });

  return { plan, ranges };
}
